import { useEffect, useMemo, useRef, useState } from "react";

import { FullScreenLoader } from "../../components/ui/loaders/full-screen-loader.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { detectPlatform, Platform } from "./detect-platform.js";
import type { DetectPlatformResult } from "./detect-platform.js";
import { PlatformContext } from "./platform.context.js";

export const PlatformProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [loading, setLoading] = useState(true);
  const detectionStarted = useRef(false);
  const [detectionResult, setDetectionResult] =
    useState<DetectPlatformResult | null>(null);

  useEffect(() => {
    const setupPlatform = async () => {
      if (detectionStarted.current) {
        return;
      }

      try {
        detectionStarted.current = true;

        const result = await detectPlatform();

        console.log("[PlatformProvider] Detection completed", {
          result,
        });

        setDetectionResult(result);
      } catch (error) {
        recordErrorInSentry(error);
        setDetectionResult({
          hostApplication: null,
          platform: Platform.Web,
          teamsClientVariant: null,
        });
      } finally {
        detectionStarted.current = false;
        setLoading(false);
      }
    };

    setupPlatform();
  }, []);

  const state = useMemo(
    () => ({
      __ctx_init: !loading,
      ...(detectionResult ?? {
        hostApplication: null,
        platform: Platform.Web,
        teamsClientVariant: null,
      }),
    }),
    [detectionResult, loading]
  );

  if (loading) {
    return <FullScreenLoader message="Detecting device" />;
  }

  return (
    <PlatformContext.Provider value={state}>
      {children}
    </PlatformContext.Provider>
  );
};
